import type { WindowName, WindowsInfo } from '@/api/types';
import { Button } from '@/ui/Button';
import { INPUT_CLASS } from '@/ui/Field';
import { Select, type SelectOption } from '@/ui/Select';
import {
  NO_FILTERS,
  hasFilters,
  type JoinedFilter,
  type PlayerFilterOptions,
  type PlayerFilters,
} from './player-filters';
import { WINDOWS, windowHeading } from './windows';

const JOINED_OPTIONS: SelectOption<JoinedFilter>[] = [
  { value: '', label: 'All players' },
  { value: 'joined', label: 'Joined' },
  { value: 'notJoined', label: 'Not joined' },
];

function windowOptions(windows: WindowsInfo): SelectOption<WindowName | ''>[] {
  return [
    { value: '', label: 'Not filtered' },
    ...WINDOWS.map((window) => ({ value: window.key, label: windowHeading(windows, window.key, window.label) })),
  ];
}

const allOr = (allLabel: string, values: string[]): SelectOption<string>[] => [
  { value: '', label: allLabel },
  ...values.map((value) => ({ value, label: value })),
];

interface PlayerFilterBarProps {
  filters: PlayerFilters;
  options: PlayerFilterOptions;
  windows: WindowsInfo;
  onChange(filters: PlayerFilters): void;
}

// A choice with a single value filters nothing, so its dropdown is left out rather than shown inert.
export function PlayerFilterBar({ filters, options, windows, onChange }: PlayerFilterBarProps) {
  const set = <F extends keyof PlayerFilters>(field: F, value: PlayerFilters[F]) =>
    onChange({ ...filters, [field]: value });

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <input
        type="search"
        aria-label="Filter players"
        className={`${INPUT_CLASS} max-w-sm`}
        placeholder="Filter by player id, username, email or agent"
        value={filters.search}
        onChange={(event) => set('search', event.target.value)}
      />
      {options.agents.length > 1 && (
        <Select
          label="Agent"
          value={filters.agent}
          options={allOr('All agents', options.agents)}
          onChange={(value) => set('agent', value)}
        />
      )}
      <Select
        label="Status"
        value={filters.joined}
        options={JOINED_OPTIONS}
        onChange={(value) => set('joined', value)}
      />
      <Select
        label="Deposited in"
        value={filters.depositedIn}
        options={windowOptions(windows)}
        onChange={(value) => set('depositedIn', value)}
      />
      <Select
        label="Active in"
        value={filters.activeIn}
        options={windowOptions(windows)}
        onChange={(value) => set('activeIn', value)}
      />
      {options.countries.length > 1 && (
        <Select
          label="Country"
          value={filters.country}
          options={allOr('All countries', options.countries)}
          onChange={(value) => set('country', value)}
        />
      )}
      {options.currencies.length > 1 && (
        <Select
          label="Currency"
          value={filters.currency}
          options={allOr('All currencies', options.currencies)}
          onChange={(value) => set('currency', value)}
        />
      )}
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-slate-300"
          checked={filters.hideTest}
          onChange={(event) => set('hideTest', event.target.checked)}
        />
        Hide test accounts
      </label>
      {hasFilters(filters) && (
        <Button variant="secondary" className="px-3 py-1.5" onClick={() => onChange(NO_FILTERS)}>
          Clear filters
        </Button>
      )}
    </div>
  );
}
